import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { chatbotService } from '../services/chatbotService';
import { toast } from 'react-toastify';
import ChatbotConfigHeader from '../components/chatbot/layout/ChatbotConfigHeader';
import ChatbotConfigTabs from '../components/chatbot/layout/ChatbotConfigTabs';
import GeneralTab from '../components/chatbot/tabs/GeneralTab';
import ThemesTab from '../components/chatbot/tabs/ThemesTab';
import PlaygroundTab from '../components/chatbot/tabs/PlaygroundTab';
import ConnectTab from '../components/chatbot/tabs/ConnectTab';
import ConversationsTab from '../components/chatbot/tabs/ConversationsTab';
import { TabId, TabDef } from '../components/chatbot/types/chatbot.types';
import {
  Cog6ToothIcon,
  SwatchIcon,
  BeakerIcon,
  LinkIcon,
  ChatBubbleLeftRightIcon,
} from '@heroicons/react/24/outline';

const ChatbotConfig: React.FC = () => {
  const { chatbotId } = useParams<{ chatbotId: string }>();
  const navigate = useNavigate();

  const [active, setActive] = useState<TabId>('general');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [chatbot, setChatbot] = useState<any>(null);

  useEffect(() => {
    if (!chatbotId) return;

    let mounted = true;
    setIsLoading(true);
    setError(null);

    (async () => {
      try {
        const resp = await chatbotService.getChatbotById(encodeURIComponent(chatbotId));
        const payload = (resp && resp.data) ? resp.data : resp;
        if (mounted) setChatbot(payload);
      } catch (err: any) {
        console.error('Failed to fetch chatbot', err);
        if (mounted) {
          setError(err?.message ?? 'Failed to load chatbot');
          toast.error('Failed to load chatbot');
        }
      } finally {
        if (mounted) setIsLoading(false);
      }
    })();

    return () => { mounted = false; };
  }, [chatbotId]);

  const tabs: TabDef[] = [
    { id: 'general', label: 'General', icon: Cog6ToothIcon, component: GeneralTab },
    { id: 'themes', label: 'Themes', icon: SwatchIcon, component: ThemesTab },
    { id: 'playground', label: 'Playground', icon: BeakerIcon, component: PlaygroundTab },
    { id: 'connect', label: 'Connect', icon: LinkIcon, component: ConnectTab },
    { id: 'conversations', label: 'Conversations', icon: ChatBubbleLeftRightIcon, component: ConversationsTab },
  ];

  const ActiveComponent = tabs.find((t) => t.id === active)?.component;

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-agentic-white-off via-agentic-primary-blue/5 to-agentic-accent-purple/10 flex items-center justify-center">
        <div className="text-agentic-text-secondary">Loading chatbot configuration...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-agentic-white-off via-agentic-primary-blue/5 to-agentic-accent-purple/10 flex items-center justify-center">
        <div className="text-red-500">Error: {error}</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-agentic-white-off via-agentic-primary-blue/5 to-agentic-accent-purple/10">
      <ChatbotConfigHeader chatbot={chatbot} onBack={() => navigate('/chatbots')} />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <ChatbotConfigTabs tabs={tabs} activeTab={active} onTabChange={setActive} />

        <div className="bg-white rounded-xl border shadow-sm">
          {ActiveComponent && chatbot && <ActiveComponent chatbot={chatbot} />}
        </div>
      </div>
    </div>
  );
};

export default ChatbotConfig;
